// Feature grid — every card carries a small live demo of the real feature
// instead of a screenshot or an icon.
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { EASE_OUT, Reveal } from './primitives';
import { useT } from '../../../i18n';
import './FeatureBento.css';

// Ticks while the element is on screen (and never under reduced motion).
function useTicker(ms, length) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: '-10% 0px' });
  const reduce = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!inView || reduce) return undefined;
    const id = setInterval(() => setI((v) => (v + 1) % length), ms);
    return () => clearInterval(id);
  }, [inView, reduce, ms, length]);
  return [ref, i];
}

const messages = {
  rules: {
    tr: ['Kişiye özel imza', 'Birincil şablon', 'Grup önceliği', 'Kurum varsayılanı'],
    en: ['Personal signature', 'Primary template', 'Group priority', 'Organization default'],
  },
  ruleCases: {
    tr: [
      { who: 'Ayşe Yılmaz', level: 0, result: 'CEO Ofisi · özel imza' },
      { who: 'Can Öztürk', level: 1, result: 'Dış Ticaret · EN' },
      { who: 'Deniz Kara', level: 2, result: 'Pazarlama (öncelik 1)' },
      { who: 'Yeni çalışan', level: 3, result: 'Kurumsal Standart' },
    ],
    en: [
      { who: 'Ayşe Yılmaz', level: 0, result: 'CEO Office · custom signature' },
      { who: 'Can Öztürk', level: 1, result: 'Foreign Trade · DE' },
      { who: 'Deniz Kara', level: 2, result: 'Marketing (priority 1)' },
      { who: 'New hire', level: 3, result: 'Corporate Standard' },
    ],
  },
  sender: { tr: 'Gönderen', en: 'Sender' },
  ruleNone: { tr: 'yok', en: 'none' },
  ruleChecking: { tr: 'kontrol…', en: 'checking…' },
  days: { tr: ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'], en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] },
  campaigns: {
    tr: [
      { name: 'Kış Kampanyası', from: 0, to: 4, tone: 'a' },
      { name: 'Fuar Daveti', from: 5, to: 9, tone: 'b' },
      { name: 'Yılbaşı Mesajı', from: 9, to: 13, tone: 'c' },
    ],
    en: [
      { name: 'Winter Campaign', from: 0, to: 4, tone: 'a' },
      { name: 'Trade Fair Invite', from: 5, to: 9, tone: 'b' },
      { name: 'New Year Message', from: 9, to: 13, tone: 'c' },
    ],
  },
  todayInSignature: { tr: 'Bugün imzada', en: 'In signatures today' },
  fields: {
    tr: [
      ['{{DisplayName}}', 'Ayşe Yılmaz'],
      ['{{JobTitle}}', 'Satış Müdürü'],
      ['{{Department}}', 'Satış'],
      ['{{Mobile}}', '+90 532 555 01 24'],
      ['{{Email}}', 'ayse.yilmaz@nordalojistik.com.tr'],
    ],
    en: [
      ['{{DisplayName}}', 'Ayşe Yılmaz'],
      ['{{JobTitle}}', 'Sales Manager'],
      ['{{Department}}', 'Sales'],
      ['{{Mobile}}', '+90 532 555 01 24'],
      ['{{Email}}', 'ayse.yilmaz@nordalojistik.com.tr'],
    ],
  },
  log: {
    tr: [
      ['ayse.yilmaz', 'Outlook Web'],
      ['can.ozturk', 'Outlook iOS'],
      ['zeynep.arslan', 'Yeni Outlook'],
      ['mert.demir', 'Outlook Masaüstü'],
      ['elif.sahin', 'Outlook Android'],
      ['deniz.kara', 'Outlook Mac'],
    ],
    en: [
      ['ayse.yilmaz', 'Outlook Web'],
      ['can.ozturk', 'Outlook iOS'],
      ['zeynep.arslan', 'New Outlook'],
      ['mert.demir', 'Outlook Desktop'],
      ['elif.sahin', 'Outlook Android'],
      ['deniz.kara', 'Outlook Mac'],
    ],
  },
  addedThisWeek: { tr: 'bu hafta eklenen imza', en: 'signatures added this week' },
  disclaimers: {
    tr: [
      ['Hukuk', 'Bu ileti avukat-müvekkil gizliliği kapsamındadır.'],
      ['Finans', 'Bu e-posta yatırım tavsiyesi niteliği taşımaz.'],
      ['İK', 'Kişisel verileriniz KVKK kapsamında işlenmektedir.'],
    ],
    en: [
      ['Legal', 'This message is protected by attorney–client privilege.'],
      ['Finance', 'This email does not constitute investment advice.'],
      ['HR', 'Your personal data is processed in line with data protection law.'],
    ],
  },
  assets: {
    tr: { banner: 'Kış Kampanyası', bannerFile: 'banner-kis.png', social: 'sosyal-ikonlar', badgeFile: 'sertifika.png' },
    en: { banner: 'Winter Campaign', bannerFile: 'banner-winter.png', social: 'social-icons', badgeFile: 'certificate.png' },
  },
  cards: {
    tr: {
      rules: ['Kural motoru', 'Kim hangi imzayı kullanır? Tek kural, sıfır sürpriz.', 'Kişiye özel imza, birincil şablon ve grup öncelikleri sırayla değerlendirilir; her çalışanın imzası tek ve öngörülebilir bir zincire bağlanır.'],
      calendar: ['Kampanyalar', 'Banner’ları bir kez planlayın.', 'İçerik takvimine koyduğunuz kampanya, tarihi gelince seçtiğiniz grupların imzasında kendiliğinden yayına girer ve biter.'],
      sync: ['Dizin senkronu', 'Unvan değişti mi? İmza da değişti.', 'Ad, unvan, departman ve telefon Microsoft 365 dizininizden gelir. Kimse imzasını elle güncellemez, kimse eski unvanla mail atmaz.'],
      log: ['Teslimat günlüğü', 'Her imza kayıt altında.', 'Hangi kullanıcıya, hangi istemcide, hangi şablonun eklendiğini panelden izleyin. Kayıtlar KVKK’ya uygun olarak 90 gün saklanır.'],
      lang: ['7 dil', 'Her alıcıya kendi dilinde.', 'Şablonların Türkçe, İngilizce, Almanca, Fransızca, Rusça, Rumence ve Arapça varyantları.'],
      disc: ['Yasal uyarılar', 'Departmana özel hukuki metin.', 'Her yasal uyarı ilgili gruplara hedeflenir; imzanın üstünde ya da altında, otomatik.'],
      assets: ['Marka varlıkları', 'Görseller hiç kırılmaz.', 'Logo ve banner’lar merkezde barındırılır; Outlook yanıt zincirlerinde bile imzanız eksiksiz görünür.'],
    },
    en: {
      rules: ['Rules engine', 'Who gets which signature? One rule, zero surprises.', 'Personal signatures, primary templates and group priorities are evaluated in order, so every employee’s signature resolves through a single, predictable chain.'],
      calendar: ['Campaigns', 'Plan your banners once.', 'Put a campaign on the content calendar and it goes live in the chosen groups’ signatures on its start date — and comes down on its end date.'],
      sync: ['Directory sync', 'New job title? New signature.', 'Name, title, department and phone come from your Microsoft 365 directory. Nobody updates their signature by hand, and nobody emails with an old title.'],
      log: ['Delivery log', 'Every signature, on record.', 'See which template was added for which user in which client, right from the panel. Records are kept for 90 days, then deleted.'],
      lang: ['7 languages', 'Speak each recipient’s language.', 'Template variants in Turkish, English, German, French, Russian, Romanian and Arabic.'],
      disc: ['Disclaimers', 'Legal text, per department.', 'Each disclaimer targets the relevant groups and lands above or below the signature, automatically.'],
      assets: ['Brand assets', 'Images that never break.', 'Logos and banners are hosted centrally, so your signature renders intact even deep in Outlook reply chains.'],
    },
  },
  extras: {
    tr: ['Microsoft ile tek tık giriş', 'Rol bazlı yetkilendirme', 'Denetim günlüğü', 'Olumsuz geri bildirim alarmı', 'Çoklu alan adı', 'İmzam self-servis sayfası', 'Canlı şablon önizleme', 'Grup bazlı kampanya hedefleme'],
    en: ['One-click Microsoft sign-in', 'Role-based access', 'Audit log', 'Negative feedback alerts', 'Multiple domains', 'Self-service My Signature page', 'Live template preview', 'Group-targeted campaigns'],
  },
  andMore: { tr: 'Ve dahası', en: 'And more' },
};

/* ── Rules engine ───────────────────────────────────────────── */
function RulesDemo() {
  const { t } = useT(messages);
  const ruleCases = t('ruleCases');
  const [ref, tick] = useTicker(700, ruleCases.length * 6);
  const caseIdx = Math.floor(tick / 6);
  const step = tick % 6;
  const c = ruleCases[caseIdx];
  const scan = Math.min(step, c.level);
  const locked = step > c.level;
  return (
    <div className="lp-fb-rules" ref={ref}>
      <div className="lp-fb-rules-who">
        <span className="lp-fb-chip">{t('sender')}</span>
        <AnimatePresence mode="wait">
          <motion.strong key={c.who} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            {c.who}
          </motion.strong>
        </AnimatePresence>
      </div>
      <ol className="lp-fb-rules-list">
        {t('rules').map((r, i) => {
          const state = i < scan || (locked && i < c.level) ? 'is-skip' : i === scan ? (locked ? 'is-match' : 'is-scan') : '';
          return (
            <li key={r} className={state}>
              <span className="lp-fb-rules-n">{i + 1}</span>
              <span className="lp-fb-rules-name">{r}</span>
              <span className="lp-fb-rules-state">
                {state === 'is-match'
                  ? <><span className="lp-tick lp-fb-rules-tick" aria-hidden="true" />{c.result}</>
                  : state === 'is-skip' ? t('ruleNone') : state === 'is-scan' ? t('ruleChecking') : ''}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ── Campaign calendar ─────────────────────────────────────── */
function CalendarDemo() {
  const { t } = useT(messages);
  const campaigns = t('campaigns');
  const [ref, day] = useTicker(900, 14);
  const active = [...campaigns].reverse().find((c) => day >= c.from && day <= c.to);
  return (
    <div className="lp-fb-cal" ref={ref}>
      <div className="lp-fb-cal-preview">
        <span className="lp-fb-chip">{t('todayInSignature')}</span>
        <AnimatePresence mode="wait">
          <motion.span
            key={active ? active.name : 'none'}
            className={`lp-fb-cal-banner tone-${active ? active.tone : 'none'}`}
            initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
            transition={{ duration: 0.45, ease: EASE_OUT }}
          >
            {active ? active.name : '—'}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="lp-fb-cal-grid">
        {t('days').map((d) => <span className="lp-fb-cal-dow" key={d}>{d}</span>)}
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} className={`lp-fb-cal-day w${i < 7 ? 0 : 1} ${i === day ? 'is-today' : ''} ${i < day ? 'is-past' : ''}`}>{i + 8}</span>
        ))}
        {campaigns.map((c, ci) => {
          // split a range across the two week rows
          const segs = [];
          for (let w = 0; w < 2; w += 1) {
            const s = Math.max(c.from, w * 7);
            const e = Math.min(c.to, w * 7 + 6);
            if (s <= e) segs.push({ w, s: s - w * 7, e: e - w * 7 });
          }
          return segs.map((sg) => (
            <span
              key={`${ci}-${sg.w}`}
              className={`lp-fb-cal-bar tone-${c.tone} lane-${ci % 2} row-${sg.w} ${active === c ? 'is-live' : ''}`}
              style={{ gridColumn: `${sg.s + 1} / ${sg.e + 2}` }}
            />
          ));
        })}
      </div>
    </div>
  );
}

/* ── Directory sync ────────────────────────────────────────── */
const FIELD_COUNT = 5;

function SyncDemo() {
  const { t } = useT(messages);
  const [ref, tick] = useTicker(650, FIELD_COUNT + 4);
  return (
    <div className="lp-fb-sync" ref={ref}>
      <div className="lp-fb-sync-head">
        <span className="lp-fb-sync-ms" aria-hidden="true"><i /><i /><i /><i /></span>
        <span>Microsoft Entra ID</span>
        <span className={`lp-fb-sync-spin ${tick < FIELD_COUNT ? 'is-on' : ''}`}>↻</span>
      </div>
      <ul>
        {t('fields').map(([k, v], i) => (
          <li key={k} className={i < tick ? 'is-resolved' : ''}>
            <code>{k}</code>
            <span className="lp-fb-sync-arrow">→</span>
            <span className="lp-fb-sync-val">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Live delivery log ─────────────────────────────────────── */
function DeliveryDemo() {
  const { t, formatNumber } = useT(messages);
  const log = t('log');
  const [ref, tick] = useTicker(1200, 600);
  const rows = Array.from({ length: 4 }).map((_, i) => {
    const n = tick - i;
    const [u, c] = log[((n % log.length) + log.length) % log.length];
    const min = 41 + n;
    return { id: n, u, c, t: `09:${String(min % 60).padStart(2, '0')}` };
  });
  return (
    <div className="lp-fb-log" ref={ref}>
      <div className="lp-fb-log-stat">
        <strong>{formatNumber(1280 + tick * 3)}</strong>
        <span>{t('addedThisWeek')}</span>
      </div>
      {/* popLayout takes the leaving row out of flow immediately, so the
          list never holds 5 rows mid-transition (the card used to jump). */}
      <ul>
        <AnimatePresence initial={false} mode="popLayout">
          {rows.map((r) => (
            <motion.li
              key={r.id}
              layout
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              transition={{ duration: 0.45, ease: EASE_OUT }}
            >
              <span className="lp-fb-log-t">{r.t}</span>
              <span className="lp-fb-log-u">{r.u}</span>
              <span className="lp-fb-log-c">{r.c}</span>
              <span className="lp-tick lp-fb-log-ok" aria-hidden="true" />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </div>
  );
}

/* ── Languages ─────────────────────────────────────────────── */
const LANGS = [
  ['TR', 'Saygılarımla'],
  ['EN', 'Best regards'],
  ['DE', 'Mit freundlichen Grüßen'],
  ['FR', 'Cordialement'],
  ['RU', 'С уважением'],
  ['RO', 'Cu stimă'],
  ['AR', 'مع أطيب التحيات'],
];

function LanguageDemo() {
  const [ref, i] = useTicker(1500, LANGS.length);
  return (
    <div className="lp-fb-lang" ref={ref}>
      <div className="lp-fb-lang-stage">
        <AnimatePresence mode="wait">
          <motion.span
            key={LANGS[i][0]}
            dir={LANGS[i][0] === 'AR' ? 'rtl' : 'ltr'}
            initial={{ y: '60%', opacity: 0, rotateX: -60 }}
            animate={{ y: '0%', opacity: 1, rotateX: 0 }}
            exit={{ y: '-60%', opacity: 0, rotateX: 60 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
          >
            {LANGS[i][1]}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="lp-fb-lang-codes">
        {LANGS.map(([code], j) => <span key={code} className={j === i ? 'is-on' : ''}>{code}</span>)}
      </div>
    </div>
  );
}

/* ── Group disclaimers ─────────────────────────────────────── */
const DISCLAIMER_COUNT = 3;

function DisclaimerDemo() {
  const { t } = useT(messages);
  const disclaimers = t('disclaimers');
  const [ref, i] = useTicker(1900, DISCLAIMER_COUNT);
  return (
    <div className="lp-fb-disc" ref={ref}>
      <div className="lp-fb-disc-groups">
        {disclaimers.map(([g], j) => <span key={g} className={j === i ? 'is-on' : ''}>{g}</span>)}
      </div>
      <div className="lp-fb-disc-mail">
        <span className="lp-fb-disc-line" />
        <span className="lp-fb-disc-line is-short" />
        <AnimatePresence mode="wait">
          <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4 }}>
            {disclaimers[i][1]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── Brand assets ──────────────────────────────────────────── */
function AssetsDemo() {
  const { t } = useT(messages);
  const a = t('assets');
  return (
    <div className="lp-fb-assets">
      <div className="lp-fb-asset is-logo"><span>N</span><small>logo.png</small></div>
      <div className="lp-fb-asset is-banner"><span>{a.banner}</span><small>{a.bannerFile}</small></div>
      <div className="lp-fb-asset is-social"><span><i /><i /><i /></span><small>{a.social}</small></div>
      <div className="lp-fb-asset is-badge"><span>ISO 9001</span><small>{a.badgeFile}</small></div>
    </div>
  );
}

const CARDS = [
  { key: 'rules', span: 'w7', demo: <RulesDemo /> },
  { key: 'calendar', span: 'w5', demo: <CalendarDemo /> },
  { key: 'sync', span: 'w6', demo: <SyncDemo /> },
  { key: 'log', span: 'w6', demo: <DeliveryDemo /> },
  { key: 'lang', span: 'w4', demo: <LanguageDemo /> },
  { key: 'disc', span: 'w4', demo: <DisclaimerDemo /> },
  { key: 'assets', span: 'w4', demo: <AssetsDemo /> },
];

export function FeatureBento() {
  const { t } = useT(messages);
  const cards = t('cards');
  return (
    <>
      <div className="lp-fb-grid">
        {CARDS.map((c, i) => (
          <Reveal key={c.key} className={`lp-fb-card ${c.span} is-${c.key}`} delay={(i % 3) * 0.08}>
            <div className="lp-fb-card-demo">{c.demo}</div>
            <div className="lp-fb-card-copy">
              <span className="lp-fb-kicker">{cards[c.key][0]}</span>
              <h3>{cards[c.key][1]}</h3>
              <p>{cards[c.key][2]}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="lp-fb-extras">
        <span className="lp-fb-extras-label">{t('andMore')}</span>
        {t('extras').map((e) => <span key={e} className="lp-fb-extra">{e}</span>)}
      </Reveal>
    </>
  );
}

export default FeatureBento;
